/**
 * Consecutive failed fetches per query (UI overhaul PR-2).
 *
 * TanStack resets its per-fetch `failureCount` when a new fetch starts, so
 * "this probe has now missed three polls in a row" — the header's
 * "something is down" signal — is not available from the query state. This
 * store listens to the query cache instead: one fetch that ends in an error
 * (after whatever retries the query allows) is one miss, a fetch that ends in
 * data resets the count, and a cancelled fetch is neither. Counting at the
 * cache means a query's retry policy cannot inflate it (a wrapper around the
 * fetcher would count every attempt).
 */
import { useCallback, useSyncExternalStore } from 'react'
import { hashKey, isCancelledError, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { isAbortError } from './requestSignal'
import { scopedKey } from './keys'
import { useOperatorScope } from './scope'

const counts = new Map<string, number>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const fn of listeners) fn()
}

function record(hash: string, ok: boolean): void {
  const next = ok ? 0 : (counts.get(hash) ?? 0) + 1
  if ((counts.get(hash) ?? 0) === next) return
  counts.set(hash, next)
  emit()
}

/** Start counting on `client`'s cache; returns the unsubscribe function. */
export function attachFailureCounter(client: QueryClient): () => void {
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return
    const action = event.action
    if (action.type === 'success') {
      if (!action.manual) record(event.query.queryHash, true)
    } else if (action.type === 'error') {
      const err: unknown = action.error
      if (!isCancelledError(err) && !isAbortError(err)) record(event.query.queryHash, false)
    }
  })
}

export function consecutiveFailures(key: QueryKey): number {
  return counts.get(hashKey(scopedKey(key))) ?? 0
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** How many polls in a row have failed for `key` (0 after any success). */
export function useConsecutiveFailures(key: QueryKey): number {
  const scope = useOperatorScope()
  const hash = hashKey(scopedKey(key, scope))
  const get = useCallback(() => counts.get(hash) ?? 0, [hash])
  return useSyncExternalStore(subscribe, get, get)
}

/** Test seam. */
export function _resetFailuresForTests(): void {
  counts.clear()
}
